import { useState } from 'react';
import { motion } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { investmentStrategies, maStrategies } from '../../mocks/investment';

export default function InvestmentPage() {
  const [activeTab, setActiveTab] = useState<'strategies' | 'ma'>('strategies');

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <section className="pt-32 pb-20 bg-gradient-to-br from-navy via-navy-dark to-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="font-display font-bold text-5xl md:text-6xl mb-6">
              Investment Strategies
            </h1>
            <p className="text-xl text-white/80 max-w-2xl mx-auto">
              Proven methods for building wealth through smart investing and strategic acquisitions
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-12 bg-white border-b border-gray-200 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={() => setActiveTab('strategies')}
              className={`px-6 py-3 rounded-lg text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
                activeTab === 'strategies'
                  ? 'bg-electric-blue text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Investment Strategies
            </button>
            <button
              onClick={() => setActiveTab('ma')}
              className={`px-6 py-3 rounded-lg text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
                activeTab === 'ma'
                  ? 'bg-electric-blue text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              M&A Methods
            </button>
          </div>
        </div>
      </section>

      {activeTab === 'strategies' && (
        <section className="py-20 bg-gradient-to-br from-gray-50 to-baby-blue/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {investmentStrategies.map((strategy, index) => (
                <motion.div
                  key={strategy.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="font-display font-bold text-2xl text-navy mb-2">
                        {strategy.title}
                      </h3>
                      <div className="flex items-center gap-3 text-sm">
                        <span className="px-3 py-1 bg-electric-blue/20 text-electric-blue rounded-full font-medium">
                          {strategy.category}
                        </span>
                        <span className="px-3 py-1 bg-baby-blue/30 text-navy rounded-full font-medium">
                          {strategy.difficulty}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="mb-4 pb-4 border-b border-gray-200">
                    <div className="text-sm text-gray-600 mb-1">Timeframe</div>
                    <div className="font-semibold text-navy">{strategy.timeframe}</div>
                  </div>

                  <p className="text-sm text-gray-700 mb-6 leading-relaxed">{strategy.description}</p>

                  <div className="mb-6">
                    <h4 className="font-semibold text-sm text-navy mb-3">Key Principles:</h4>
                    <ul className="space-y-2">
                      {strategy.keyPrinciples.map((principle, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <i className="ri-check-line text-base text-electric-blue"></i>
                          </div>
                          <span>{principle}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="bg-gradient-to-r from-baby-blue/20 to-electric-blue/10 rounded-lg p-4">
                    <div className="text-xs text-gray-600 mb-1">Expected Return</div>
                    <div className="font-bold text-lg text-navy">{strategy.expectedReturn}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {activeTab === 'ma' && (
        <section className="py-20 bg-gradient-to-br from-gray-50 to-baby-blue/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-8">
              {maStrategies.map((strategy, index) => (
                <motion.div
                  key={strategy.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all"
                >
                  <div className="flex flex-col lg:flex-row gap-8">
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="font-display font-bold text-2xl text-navy mb-2">
                            {strategy.title}
                          </h3>
                          <div className="flex items-center gap-3 text-sm">
                            <span className="px-3 py-1 bg-electric-blue/20 text-electric-blue rounded-full font-medium">
                              {strategy.type}
                            </span>
                            <span className="px-3 py-1 bg-baby-blue/30 text-navy rounded-full font-medium">
                              {strategy.capitalRequired}
                            </span>
                          </div>
                        </div>
                      </div>

                      <p className="text-sm text-gray-700 mb-6 leading-relaxed">{strategy.description}</p>

                      <div>
                        <h4 className="font-semibold text-sm text-navy mb-3">Implementation Steps:</h4>
                        <ol className="space-y-3">
                          {strategy.steps.map((step, idx) => (
                            <li key={idx} className="flex items-start gap-3 text-sm text-gray-600">
                              <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 bg-electric-blue text-white rounded-full text-xs font-bold">
                                {idx + 1}
                              </div>
                              <span className="pt-0.5">{step}</span>
                            </li>
                          ))}
                        </ol>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-navy to-navy-dark text-white rounded-2xl p-12 text-center"
          >
            <div className="w-16 h-16 flex items-center justify-center bg-electric-blue rounded-full mx-auto mb-6">
              <i className="ri-lightbulb-line text-3xl text-white"></i>
            </div>
            <h2 className="font-display font-bold text-3xl md:text-4xl mb-4">
              Need Personalized Guidance?
            </h2>
            <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
              Book a consultation to discuss investment strategies tailored to your financial goals and risk tolerance
            </p>
            <a
              href="/booking"
              className="inline-block px-8 py-4 bg-electric-blue text-white rounded-lg text-base font-semibold hover:bg-blue-500 transition-colors whitespace-nowrap cursor-pointer"
            >
              Schedule Consultation
            </a>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}